// src/components/CompanyManager.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Divider,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  AlertTitle
} from '@mui/material';
import {
  Building2,
  Mail,
  Globe,
  Phone,
  MapPin,
  User,
  CreditCard,
  Info,
  Trash2,
  Plus,
  ChevronDown,
  Users,
  Calendar
} from 'lucide-react';
import axios from 'axios';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import CompanyBillingManager from './CompanyBillingManager';


const API_URL = process.env.REACT_APP_API_URL;

const CompanyManager = ({ open, onClose, company }) => {
  const isEditMode = !!company;

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    domain: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    website: '',
    status: 'active',
    planType: 'Basic',
    maxUsers: 10,
    billingCycle: 'monthly',
    trialEndDate: null,
    enableAiSignature: false,
    enableSignatureTemplates: false,
    billingInfo: {
      companyName: '',
      contactName: '',
      contactEmail: '',
      vatId: '',
      billingAddress: '',
      paymentMethod: 'invoice'
    }
  });

  // Company users state
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // UI state
  const [activeAccordion, setActiveAccordion] = useState('company-details');

  // Load company data when in edit mode
  useEffect(() => {
    if (isEditMode && company) {
      // Format company data for form
      setFormData({
        name: company.name || '',
        domain: company.domain || '',
        email: company.email || '',
        phone: company.phone || '',
        address: company.address || '',
        city: company.city || '',
        state: company.state || '',
        zip: company.zip || '',
        country: company.country || '',
        website: company.website || company.domain ? `https://${company.domain}` : '',
        status: company.status || 'active',
        planType: company.planType || 'Basic',
        maxUsers: company.maxUsers || 10,
        billingCycle: company.billingCycle || 'monthly',
        trialEndDate: company.trialEnds ? new Date(company.trialEnds) : null,
        enableAiSignature: company.enableAiSignature || false,
        enableSignatureTemplates: company.enableSignatureTemplates || false,
        billingInfo: company.billingInfo || {
          companyName: company.name || '',
          contactName: '',
          contactEmail: '',
          vatId: '',
          billingAddress: '',
          paymentMethod: 'invoice'
        }
      });

      // If in edit mode, fetch company users
      fetchCompanyUsers(company.id);
    } else {
      // Reset form for new company
      setFormData({
        name: '',
        domain: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        country: '',
        website: '',
        status: 'active',
        planType: 'Basic',
        maxUsers: 10,
        billingCycle: 'monthly',
        trialEndDate: null,
        enableAiSignature: false,
        enableSignatureTemplates: false,
        billingInfo: {
          companyName: '',
          contactName: '',
          contactEmail: '',
          vatId: '',
          billingAddress: '',
          paymentMethod: 'invoice'
        }
      });
      setUsers([]);
    }
  }, [isEditMode, company]);

  // Fetch company users
  const fetchCompanyUsers = async (companyId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/companies/${companyId}/users`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      setUsers(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching company users:', err);
      setError('Failed to load company users');

      // If in development mode, provide mock data
      if (process.env.NODE_ENV === 'development') {
        setUsers(generateMockUsers());
      }
    } finally {
      setLoading(false);
    }
  };

  // Generate mock users for development
  const generateMockUsers = () => {
    return [
      { id: 1, name: 'John Smith', email: 'john@example.com', role: 'admin', status: 'active', lastLogin: '2023-03-10T14:30:00Z' },
      { id: 2, name: 'Sarah Johnson', email: 'sarah@example.com', role: 'user', status: 'active', lastLogin: '2023-03-12T09:15:00Z' },
      { id: 3, name: 'Mike Williams', email: 'mike@example.com', role: 'user', status: 'active', lastLogin: '2023-03-08T11:45:00Z' },
      { id: 4, name: 'Emily Davis', email: 'emily@example.com', role: 'user', status: 'inactive', lastLogin: '2023-02-28T16:20:00Z' }
    ];
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  // Handle billing info changes
  const handleBillingInfoChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      billingInfo: {
        ...prevData.billingInfo,
        [name]: value
      }
    }));
  };

  // Handle trial end date change
  const handleTrialEndDateChange = (date) => {
    setFormData(prevData => ({
      ...prevData,
      trialEndDate: date
    }));
  };

  // Handle accordion change
  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setActiveAccordion(isExpanded ? panel : false);
  };

  // Toggle user status
  const toggleUserStatus = async (userId, currentStatus) => {
    if (!isEditMode) return;

    try {
      setLoading(true);
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';

      await axios.put(`${API_URL}/api/admin/users/${userId}/status`,
        { status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      // Update local users state
      setUsers(prevUsers =>
        prevUsers.map(user =>
          user.id === userId ? { ...user, status: newStatus } : user
        )
      );

      setError(null);
    } catch (err) {
      console.error('Error updating user status:', err);
      setError('Failed to update user status');
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      setLoading(true);
      setSaveSuccess(false);

      // Format data for API
      const apiData = {
        ...formData,
        status: formData.status,
        trialEnds: formData.status === 'trial' && formData.trialEndDate ?
          formData.trialEndDate.toISOString() : null
      };

      let response;

      if (isEditMode) {
        // Update existing company
        response = await axios.put(`${API_URL}/api/admin/companies/${company.id}`,
          apiData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
      } else {
        // Create new company
        response = await axios.post(`${API_URL}/api/admin/companies`,
          apiData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
      }

      setSaveSuccess(true);
      setError(null);

      // Close dialog after short delay
      setTimeout(() => {
        onClose();
      }, 1500);

    } catch (err) {
      console.error('Error saving company:', err);
      setError(err.response?.data?.message || 'Failed to save company data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="company-dialog-title"
    >
      <DialogTitle id="company-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Building2 size={24} />
        {isEditMode ? `Edit ${company?.name}` : 'Add New Company'}
      </DialogTitle>

      <DialogContent dividers>
        {/* Success message */}
        {saveSuccess && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Company {isEditMode ? 'updated' : 'created'} successfully!
          </Alert>
        )}

        {/* Error message */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Company Details Accordion */}
        <Accordion
          expanded={activeAccordion === 'company-details'}
          onChange={handleAccordionChange('company-details')}
          elevation={0}
          sx={{ border: '1px solid #e0e0e0', mb: 2, borderRadius: 1 }}
        >
          <AccordionSummary expandIcon={<ChevronDown />}>
            <Typography variant="subtitle1" fontWeight="medium">Company Details</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="name"
                  label="Company Name"
                  value={formData.name}
                  onChange={handleChange}
                  fullWidth
                  required
                  InputProps={{
                    startAdornment: <Building2 size={18} style={{ marginRight: 8, opacity: 0.7 }} />
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="domain"
                  label="Domain"
                  value={formData.domain}
                  onChange={handleChange}
                  fullWidth
                  InputProps={{
                    startAdornment: <Globe size={18} style={{ marginRight: 8, opacity: 0.7 }} />
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="email"
                  label="Company Email"
                  value={formData.email}
                  onChange={handleChange}
                  fullWidth
                  InputProps={{
                    startAdornment: <Mail size={18} style={{ marginRight: 8, opacity: 0.7 }} />
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="phone"
                  label="Phone Number"
                  value={formData.phone}
                  onChange={handleChange}
                  fullWidth
                  InputProps={{
                    startAdornment: <Phone size={18} style={{ marginRight: 8, opacity: 0.7 }} />
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="address"
                  label="Address"
                  value={formData.address}
                  onChange={handleChange}
                  fullWidth
                  InputProps={{
                    startAdornment: <MapPin size={18} style={{ marginRight: 8, opacity: 0.7 }} />
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  name="city"
                  label="City"
                  value={formData.city}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  name="state"
                  label="State/Province"
                  value={formData.state}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  name="zip"
                  label="Postal/ZIP Code"
                  value={formData.zip}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="country"
                  label="Country"
                  value={formData.country}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="website"
                  label="Website"
                  value={formData.website}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Update the Subscription Settings Accordion */}
        <Accordion
          expanded={activeAccordion === 'subscription'}
          onChange={handleAccordionChange('subscription')}
          elevation={0}
          sx={{ border: '1px solid #e0e0e0', mb: 2, borderRadius: 1 }}
        >
          <AccordionSummary expandIcon={<ChevronDown />}>
            <Typography variant="subtitle1" fontWeight="medium">Subscription Settings</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    label="Status"
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="trial">Trial</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Plan Type</InputLabel>
                  <Select
                    name="planType"
                    value={formData.planType}
                    onChange={handleChange}
                    label="Plan Type"
                  >
                    <MenuItem value="Free">Free</MenuItem>
                    <MenuItem value="Basic">Basic</MenuItem>
                    <MenuItem value="Pro">Pro</MenuItem>
                    <MenuItem value="Enterprise">Enterprise</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="maxUsers"
                  label="Maximum Users"
                  type="number"
                  value={formData.maxUsers}
                  onChange={handleChange}
                  fullWidth
                  InputProps={{
                    startAdornment: <Users size={18} style={{ marginRight: 8, opacity: 0.7 }} />
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Billing Cycle</InputLabel>
                  <Select
                    name="billingCycle"
                    value={formData.billingCycle}
                    onChange={handleChange}
                    label="Billing Cycle"
                  >
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="quarterly">Quarterly</MenuItem>
                    <MenuItem value="annually">Annually</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {formData.status === 'trial' && (
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Trial End Date"
                      value={formData.trialEndDate}
                      onChange={handleTrialEndDateChange}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                      InputProps={{
                        startAdornment: <Calendar size={18} style={{ marginRight: 8, opacity: 0.7 }} />
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
              )}

              {/* Add divider for feature controls section */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Feature Availability
                </Typography>
              </Grid>

              {/* AI Signature Enable/Disable */}
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.enableAiSignature}
                      onChange={(e) => setFormData({
                        ...formData,
                        enableAiSignature: e.target.checked
                      })}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">AI-Powered Signatures</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Allow users to generate AI-recommended content for signatures
                      </Typography>
                    </Box>
                  }
                  sx={{
                    p: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    width: '100%',
                    m: 0
                  }}
                />
              </Grid>

              {/* Signature Templates Enable/Disable */}
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.enableSignatureTemplates}
                      onChange={(e) => setFormData({
                        ...formData,
                        enableSignatureTemplates: e.target.checked
                      })}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">Signature Templates</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Enable access to signature template gallery
                      </Typography>
                    </Box>
                  }
                  sx={{
                    p: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    width: '100%',
                    m: 0
                  }}
                />
              </Grid>

              {/* Info note about premium features */}
              <Grid item xs={12}>
                <Alert severity="info" icon={<Info size={18} />} sx={{ mt: 1 }}>
                  <AlertTitle>Premium Features</AlertTitle>
                  AI-Powered Signatures and Signature Templates are premium features available on Pro and Enterprise plans.
                  {formData.planType === 'Free' || formData.planType === 'Basic' ?
                    " These settings won't take effect unless the plan is upgraded." :
                    ""}
                </Alert>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Billing Information Accordion */}
        {/* <Accordion 
          expanded={activeAccordion === 'billing'} 
          onChange={handleAccordionChange('billing')}
          elevation={0}
          sx={{ border: '1px solid #e0e0e0', mb: 2, borderRadius: 1 }}
        >
          <AccordionSummary expandIcon={<ChevronDown />}>
            <Typography variant="subtitle1" fontWeight="medium">Billing Information</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="companyName"
                  label="Billing Company Name"
                  value={formData.billingInfo.companyName}
                  onChange={handleBillingInfoChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="contactName"
                  label="Billing Contact Name"
                  value={formData.billingInfo.contactName}
                  onChange={handleBillingInfoChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="contactEmail"
                  label="Billing Email"
                  value={formData.billingInfo.contactEmail}
                  onChange={handleBillingInfoChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="vatId"
                  label="VAT/Tax ID"
                  value={formData.billingInfo.vatId}
                  onChange={handleBillingInfoChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="billingAddress"
                  label="Billing Address"
                  value={formData.billingInfo.billingAddress}
                  onChange={handleBillingInfoChange}
                  fullWidth
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Payment Method</InputLabel>
                  <Select
                    name="paymentMethod"
                    value={formData.billingInfo.paymentMethod}
                    onChange={handleBillingInfoChange}
                    label="Payment Method"
                  >
                    <MenuItem value="invoice">Invoice</MenuItem>
                    <MenuItem value="credit_card">Credit Card</MenuItem>
                    <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion> */}
        {/* Billing Information Accordion */}
        <Accordion
          expanded={activeAccordion === 'billing'}
          onChange={handleAccordionChange('billing')}
          elevation={0}
          sx={{ border: '1px solid #e0e0e0', mb: 2, borderRadius: 1 }}
        >
          <AccordionSummary expandIcon={<ChevronDown />}>
            <Typography variant="subtitle1" fontWeight="medium">Billing Information</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {isEditMode ? (
              // Use the dedicated billing component when in edit mode
              <CompanyBillingManager
                companyId={company.id}
                isAdmin={true}
              />
            ) : (
              // Use the simplified form for new companies
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="companyName"
                    label="Billing Company Name"
                    value={formData.billingInfo.companyName}
                    onChange={handleBillingInfoChange}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="contactName"
                    label="Billing Contact Name"
                    value={formData.billingInfo.contactName}
                    onChange={handleBillingInfoChange}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="contactEmail"
                    label="Billing Email"
                    value={formData.billingInfo.contactEmail}
                    onChange={handleBillingInfoChange}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="vatId"
                    label="VAT/Tax ID"
                    value={formData.billingInfo.vatId}
                    onChange={handleBillingInfoChange}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="billingAddress"
                    label="Billing Address"
                    value={formData.billingInfo.billingAddress}
                    onChange={handleBillingInfoChange}
                    fullWidth
                    multiline
                    rows={2}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Payment Method</InputLabel>
                    <Select
                      name="paymentMethod"
                      value={formData.billingInfo.paymentMethod}
                      onChange={handleBillingInfoChange}
                      label="Payment Method"
                    >
                      <MenuItem value="invoice">Invoice</MenuItem>
                      <MenuItem value="credit_card">Credit Card</MenuItem>
                      <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            )}
          </AccordionDetails>
        </Accordion>
        {/* Users Section - only visible in edit mode */}
        {isEditMode && (
          <Accordion
            expanded={activeAccordion === 'users'}
            onChange={handleAccordionChange('users')}
            elevation={0}
            sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}
          >
            <AccordionSummary expandIcon={<ChevronDown />}>
              <Typography variant="subtitle1" fontWeight="medium">
                Company Users
                <Chip
                  label={users.length}
                  size="small"
                  color="primary"
                  sx={{ ml: 1 }}
                />
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Last Login</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users.length > 0 ? (
                        users.map((user) => (
                          <TableRow key={user.id}>
                            <TableCell>{user.name}</TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={user.role}
                                color={user.role === 'admin' ? 'secondary' : 'default'}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={user.status}
                                color={user.status === 'active' ? 'success' : 'default'}
                              />
                            </TableCell>
                            <TableCell>
                              {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
                            </TableCell>
                            <TableCell>
                              <Box>
                                <Button
                                  size="small"
                                  onClick={() => toggleUserStatus(user.id, user.status)}
                                >
                                  {user.status === 'active' ? 'Deactivate' : 'Activate'}
                                </Button>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Typography variant="body2" color="textSecondary">
                              No users found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {/* Button to add new user to company */}
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<Plus size={16} />}
                // This would open a separate user creation dialog
                >
                  Add Company User
                </Button>
              </Box>
            </AccordionDetails>
          </Accordion>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || !formData.name.trim()}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {isEditMode ? 'Update Company' : 'Create Company'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompanyManager;